import React from 'react';
import { Alert, Badge, Button, Form, Accordion } from 'react-bootstrap';
import type { User } from '../types';
import type { NotificationPreferences } from '../services/api';

export interface NotificationPreferencesPanelProps {
  userInfo: Partial<User>;
  notificationPreferences: NotificationPreferences | null;
  loadingPreferences: boolean;
  updatePreference: (
    category: keyof NotificationPreferences,
    field: string,
    value: boolean,
    subField?: string
  ) => void;
  onRequestBindEmail: () => void;
}

export const NotificationPreferencesPanel: React.FC<NotificationPreferencesPanelProps> = ({
  userInfo,
  notificationPreferences,
  loadingPreferences,
  updatePreference,
  onRequestBindEmail,
}) => {
  return (
    <div className="mb-3">
      <p className="text-muted small mb-0">配置仪器、文献、耗材、审批与畅所欲言等通知渠道。</p>
      {!userInfo.email && (
        <Alert variant="warning" className="mt-2">
          <Alert.Heading className="h6">未绑定邮箱</Alert.Heading>
          <p className="mb-2 small">您尚未绑定邮箱，无法接收邮件通知。请在「编辑资料」中填写邮箱并保存。</p>
          <Button variant="outline-primary" size="sm" onClick={() => onRequestBindEmail()}>
            去绑定邮箱
          </Button>
        </Alert>
      )}
      {loadingPreferences ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">加载中...</span>
          </div>
        </div>
      ) : notificationPreferences ? (
        <Accordion defaultActiveKey="0" className="mt-3">
              {/* 仪器管理通知 */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span>仪器管理通知</span>
                    <Badge bg={notificationPreferences.instrument.enabled ? 'success' : 'secondary'} className="ms-2">
                      {notificationPreferences.instrument.enabled ? '已启用' : '已关闭'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    <Form.Check
                      type="switch"
                      id="instrument-enabled"
                      label="启用仪器管理通知"
                      checked={notificationPreferences.instrument.enabled}
                      onChange={(e) => updatePreference('instrument', 'enabled', e.target.checked)}
                    />
                  </div>
                  {notificationPreferences.instrument.enabled && (
                    <>
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="instrument-email"
                          label="启用邮件通知"
                          checked={notificationPreferences.instrument.emailEnabled}
                          onChange={(e) => updatePreference('instrument', 'emailEnabled', e.target.checked)}
                          disabled={!userInfo.email}
                        />
                        {!userInfo.email && (
                          <Form.Text className="text-muted small d-block mt-1">
                            <span className="text-warning">⚠️ 请先绑定邮箱才能启用邮件通知</span>
                          </Form.Text>
                        )}
                      </div>
                      <div className="ms-4">
                        <h6 className="small mb-2">通知场景：</h6>
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-created"
                          label="新预约申请（管理员）"
                          checked={notificationPreferences.instrument.scenarios.reservationCreated}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationCreated')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-approved"
                          label="预约批准通知"
                          checked={notificationPreferences.instrument.scenarios.reservationApproved}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationApproved')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-rejected"
                          label="预约拒绝通知"
                          checked={notificationPreferences.instrument.scenarios.reservationRejected}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationRejected')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-reminder"
                          label="预约开始前30分钟提醒"
                          checked={notificationPreferences.instrument.scenarios.reservationReminder}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationReminder')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-cancelled"
                          label="预约取消通知"
                          checked={notificationPreferences.instrument.scenarios.reservationCancelled}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationCancelled')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-reservation-status-changed"
                          label="预约状态变更通知"
                          checked={notificationPreferences.instrument.scenarios.reservationStatusChanged}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'reservationStatusChanged')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="instrument-maintenance-due"
                          label="维护周期到期提醒"
                          checked={notificationPreferences.instrument.scenarios.maintenanceDue ?? true}
                          onChange={(e) => updatePreference('instrument', 'scenarios', e.target.checked, 'maintenanceDue')}
                          className="mb-2"
                        />
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* 文献管理通知 */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span>文献管理通知</span>
                    <Badge bg={notificationPreferences.literature.enabled ? 'success' : 'secondary'} className="ms-2">
                      {notificationPreferences.literature.enabled ? '已启用' : '已关闭'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    <Form.Check
                      type="switch"
                      id="literature-enabled"
                      label="启用文献管理通知"
                      checked={notificationPreferences.literature.enabled}
                      onChange={(e) => updatePreference('literature', 'enabled', e.target.checked)}
                    />
                  </div>
                  {notificationPreferences.literature.enabled && (
                    <>
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="literature-email"
                          label="启用邮件通知"
                          checked={notificationPreferences.literature.emailEnabled}
                          onChange={(e) => updatePreference('literature', 'emailEnabled', e.target.checked)}
                          disabled={!userInfo.email}
                        />
                        {!userInfo.email && (
                          <Form.Text className="text-muted small d-block mt-1">
                            <span className="text-warning">⚠️ 请先绑定邮箱才能启用邮件通知</span>
                          </Form.Text>
                        )}
                      </div>
                      <div className="ms-4">
                        <h6 className="small mb-2">通知场景：</h6>
                        <Form.Check
                          type="switch"
                          id="literature-shared-folder-invite"
                          label="共享文件夹邀请"
                          checked={notificationPreferences.literature.scenarios.sharedFolderInvite}
                          onChange={(e) => updatePreference('literature', 'scenarios', e.target.checked, 'sharedFolderInvite')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="literature-shared-folder-accepted"
                          label="共享文件夹接受"
                          checked={notificationPreferences.literature.scenarios.sharedFolderAccepted}
                          onChange={(e) => updatePreference('literature', 'scenarios', e.target.checked, 'sharedFolderAccepted')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="literature-shared-folder-rejected"
                          label="共享文件夹拒绝"
                          checked={notificationPreferences.literature.scenarios.sharedFolderRejected}
                          onChange={(e) => updatePreference('literature', 'scenarios', e.target.checked, 'sharedFolderRejected')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="literature-file-shared"
                          label="文件被共享"
                          checked={notificationPreferences.literature.scenarios.fileShared}
                          onChange={(e) => updatePreference('literature', 'scenarios', e.target.checked, 'fileShared')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="literature-quota-warning"
                          label="存储配额警告"
                          checked={notificationPreferences.literature.scenarios.quotaWarning}
                          onChange={(e) => updatePreference('literature', 'scenarios', e.target.checked, 'quotaWarning')}
                          className="mb-2"
                        />
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* 耗材管理通知 */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span>耗材管理通知</span>
                    <Badge bg={notificationPreferences.consumable.enabled ? 'success' : 'secondary'} className="ms-2">
                      {notificationPreferences.consumable.enabled ? '已启用' : '已关闭'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    <Form.Check
                      type="switch"
                      id="consumable-enabled"
                      label="启用耗材管理通知"
                      checked={notificationPreferences.consumable.enabled}
                      onChange={(e) => updatePreference('consumable', 'enabled', e.target.checked)}
                    />
                  </div>
                  {notificationPreferences.consumable.enabled && (
                    <>
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="consumable-email"
                          label="启用邮件通知"
                          checked={notificationPreferences.consumable.emailEnabled}
                          onChange={(e) => updatePreference('consumable', 'emailEnabled', e.target.checked)}
                          disabled={!userInfo.email}
                        />
                        {!userInfo.email && (
                          <Form.Text className="text-muted small d-block mt-1">
                            <span className="text-warning">⚠️ 请先绑定邮箱才能启用邮件通知</span>
                          </Form.Text>
                        )}
                      </div>
                      <div className="ms-4">
                        <h6 className="small mb-2">通知场景：</h6>
                        <Form.Check
                          type="switch"
                          id="consumable-low-stock"
                          label="库存不足提醒"
                          checked={notificationPreferences.consumable.scenarios.lowStock}
                          onChange={(e) => updatePreference('consumable', 'scenarios', e.target.checked, 'lowStock')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="consumable-order-created"
                          label="订单创建通知"
                          checked={notificationPreferences.consumable.scenarios.orderCreated}
                          onChange={(e) => updatePreference('consumable', 'scenarios', e.target.checked, 'orderCreated')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="consumable-order-approved"
                          label="订单批准通知"
                          checked={notificationPreferences.consumable.scenarios.orderApproved}
                          onChange={(e) => updatePreference('consumable', 'scenarios', e.target.checked, 'orderApproved')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="consumable-order-rejected"
                          label="订单拒绝通知"
                          checked={notificationPreferences.consumable.scenarios.orderRejected}
                          onChange={(e) => updatePreference('consumable', 'scenarios', e.target.checked, 'orderRejected')}
                          className="mb-2"
                        />
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              {/* 审批通知 */}
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span>审批通知</span>
                    <Badge bg={notificationPreferences.approval.enabled ? 'success' : 'secondary'} className="ms-2">
                      {notificationPreferences.approval.enabled ? '已启用' : '已关闭'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    <Form.Check
                      type="switch"
                      id="approval-enabled"
                      label="启用审批通知"
                      checked={notificationPreferences.approval.enabled}
                      onChange={(e) => updatePreference('approval', 'enabled', e.target.checked)}
                    />
                  </div>
                  {notificationPreferences.approval.enabled && (
                    <>
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="approval-email"
                          label="启用邮件通知"
                          checked={notificationPreferences.approval.emailEnabled}
                          onChange={(e) => updatePreference('approval', 'emailEnabled', e.target.checked)}
                          disabled={!userInfo.email}
                        />
                        {!userInfo.email && (
                          <Form.Text className="text-muted small d-block mt-1">
                            <span className="text-warning">⚠️ 请先绑定邮箱才能启用邮件通知</span>
                          </Form.Text>
                        )}
                      </div>
                      <div className="ms-4">
                        <h6 className="small mb-2">通知场景：</h6>
                        <Form.Check
                          type="switch"
                          id="approval-application-submitted"
                          label="申请提交通知"
                          checked={notificationPreferences.approval.scenarios.applicationSubmitted}
                          onChange={(e) => updatePreference('approval', 'scenarios', e.target.checked, 'applicationSubmitted')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="approval-application-approved"
                          label="申请批准通知"
                          checked={notificationPreferences.approval.scenarios.applicationApproved}
                          onChange={(e) => updatePreference('approval', 'scenarios', e.target.checked, 'applicationApproved')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="approval-application-rejected"
                          label="申请拒绝通知"
                          checked={notificationPreferences.approval.scenarios.applicationRejected}
                          onChange={(e) => updatePreference('approval', 'scenarios', e.target.checked, 'applicationRejected')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="approval-pending-approval"
                          label="待审批提醒（审批人）"
                          checked={notificationPreferences.approval.scenarios.pendingApproval}
                          onChange={(e) => updatePreference('approval', 'scenarios', e.target.checked, 'pendingApproval')}
                          className="mb-2"
                        />
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="4">
                <Accordion.Header>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span>畅所欲言通知</span>
                    <Badge bg={notificationPreferences.feedback.enabled ? 'success' : 'secondary'} className="ms-2">
                      {notificationPreferences.feedback.enabled ? '已启用' : '已关闭'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    <Form.Check
                      type="switch"
                      id="feedback-enabled"
                      label="启用畅所欲言通知"
                      checked={notificationPreferences.feedback.enabled}
                      onChange={(e) => updatePreference('feedback', 'enabled', e.target.checked)}
                    />
                  </div>
                  {notificationPreferences.feedback.enabled && (
                    <>
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="feedback-email"
                          label="启用邮件通知"
                          checked={notificationPreferences.feedback.emailEnabled}
                          onChange={(e) => updatePreference('feedback', 'emailEnabled', e.target.checked)}
                          disabled={!userInfo.email}
                        />
                        {!userInfo.email && (
                          <Form.Text className="text-muted small d-block mt-1">
                            <span className="text-warning">⚠️ 请先绑定邮箱才能启用邮件通知</span>
                          </Form.Text>
                        )}
                      </div>
                      <div className="ms-4">
                        <h6 className="small mb-2">通知场景：</h6>
                        <Form.Check
                          type="switch"
                          id="feedback-new-post"
                          label="广场有新帖（按角色权限推送）"
                          checked={notificationPreferences.feedback.scenarios.newPost}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'newPost')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-official-reply"
                          label="收到官方回复"
                          checked={notificationPreferences.feedback.scenarios.officialReply}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'officialReply')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-status-accepted"
                          label="状态变为「已受理」"
                          checked={notificationPreferences.feedback.scenarios.statusAccepted}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'statusAccepted')}
                          className="mb-2"
                        />

                        <Form.Check
                          type="switch"
                          id="feedback-status-in-progress"
                          label="状态变为「处理中」"
                          checked={notificationPreferences.feedback.scenarios.statusInProgress}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'statusInProgress')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-status-resolved"
                          label="状态变为「已解决」"
                          checked={notificationPreferences.feedback.scenarios.statusResolved}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'statusResolved')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-status-rejected"
                          label="状态变为「不予受理」"
                          checked={notificationPreferences.feedback.scenarios.statusRejected}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'statusRejected')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-report-processed"
                          label="我的举报已处理"
                          checked={notificationPreferences.feedback.scenarios.reportProcessed}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'reportProcessed')}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="feedback-weekly-summary"
                          label="每周一接收匿名反馈周度总结"
                          checked={notificationPreferences.feedback.scenarios.weeklySummary}
                          onChange={(e) => updatePreference('feedback', 'scenarios', e.target.checked, 'weeklySummary')}
                          className="mb-2"
                        />
                      </div>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>

        </Accordion>
      ) : (
        <Alert variant="warning">无法加载通知偏好设置</Alert>
      )}
    </div>
  );
};
